import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { OrderItem } from '../../../graphql/query/getOrder';
import { filterItemsByEligibility } from '../../../utils/item';
import { rawString } from '../../Language/Language';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { useShipmentMetadata } from '../../../hooks/useShipmentGroups';
import { useWorkflow } from '../../../workflow/hooks';
import type { TaskComponentType } from '../../../workflow/schemaTypes';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import { EligibleItemsView } from './EligibleItemsView';
import { showMoreCutoff } from './ShowMoreButton';

export const EligibleItems: TaskComponentType = ({ handleNext }) => {
  const isMobile = useIsMobile();
  const { data, order } = useWorkflow();
  const { errorToast } = useToast();
  const { shipmentGroups, hasMultipleShipments } = useShipmentMetadata();
  const { items } = order ?? { items: [] };
  const { setValue } = useFormContext();
  const eligibleItems =
    filterItemsByEligibility(items as OrderItem[], true) ?? [];

  const notCutoff = eligibleItems.length <= showMoreCutoff;

  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setShowMore(notCutoff);
  }, [notCutoff, setShowMore]);

  if (!eligibleItems || eligibleItems.length === 0) {
    return null;
  }

  const handleInitialSelect = async (
    itemId: string,
    trackingNumber?: string,
  ) => {
    if (!itemId) {
      return errorToast({
        message:
          rawString({
            langKey: 'Global.itemSelection.eligible.navigationError',
          }) ?? '',
      });
    }

    // Set the newly selected item as the only item in itemSelection and items
    const newItem = {
      id: itemId,
      quantity: 1,
    };

    setValue('items', {
      [itemId]: {
        itemSelection: {
          ...newItem,
          ...(trackingNumber && { trackingNumber }),
        },
      },
    });

    // Reset all other values in current workflow data
    const otherValuesToReset = data
      ? Object.keys(data).filter((key) => key !== 'items')
      : [];
    for (const key of otherValuesToReset) {
      setValue(key, null);
    }

    handleNext();
  };

  return (
    <EligibleItemsView
      eligibleItems={eligibleItems}
      hasMultipleShipments={hasMultipleShipments}
      shipmentGroups={shipmentGroups}
      isMobile={isMobile}
      showMore={showMore}
      onShowMore={() => setShowMore(true)}
      onItemSelect={handleInitialSelect}
    />
  );
};
